import { useState, useMemo } from "react";
import { fightersSchedule2026, opponentColors, opponentShortNames, monthLabels, specialEventStyles, type GameDay } from "@/mocks/fightersSchedule";
import GameDetailModal from "@/pages/home/components/GameDetailModal";

const DAYS_OF_WEEK = ["日", "月", "火", "水", "木", "金", "土"];

function getGamesByDate(schedule: GameDay[]): Map<string, GameDay> {
  const map = new Map<string, GameDay>();
  schedule.forEach((g) => map.set(g.date, g));
  return map;
}

function getMonthGames(year: number, month: number, gamesMap: Map<string, GameDay>): GameDay[] {
  const result: GameDay[] = [];
  gamesMap.forEach((g) => {
    const [y, m] = g.date.split("-").map(Number);
    if (y === year && m === month) result.push(g);
  });
  result.sort((a, b) => a.date.localeCompare(b.date));
  return result;
}

function formatDateStr(y: number, m: number, d: number) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function getTicketStatus(game: GameDay, todayStr: string): { label: string; urgent: boolean } | null {
  if (!game.ticketSaleDate) return null;
  if (game.ticketSaleDate <= todayStr) {
    return { label: "発売中", urgent: false };
  }
  const saleDateObj = new Date(game.ticketSaleDate);
  const m = saleDateObj.getMonth() + 1;
  const d = saleDateObj.getDate();
  return { label: `${m}/${d}発売`, urgent: true };
}

export default function Schedule() {
  const today = new Date();
  const todayStr = formatDateStr(today.getFullYear(), today.getMonth() + 1, today.getDate());

  const [viewYear, setViewYear] = useState(2026);
  const [viewMonth, setViewMonth] = useState(() => {
    const m = today.getFullYear() === 2026 ? today.getMonth() + 1 : 3;
    return m;
  });
  const [showSpecialOnly, setShowSpecialOnly] = useState(false);
  const [selectedGame, setSelectedGame] = useState<GameDay | null>(null);

  const gamesMap = useMemo(() => getGamesByDate(fightersSchedule2026), []);

  const monthGames = useMemo(
    () => getMonthGames(viewYear, viewMonth, gamesMap),
    [viewYear, viewMonth, gamesMap],
  );

  const displayedGames = useMemo(
    () => (showSpecialOnly ? monthGames.filter((g) => g.specialEvent) : monthGames),
    [monthGames, showSpecialOnly],
  );

  const goToPrevMonth = () => {
    if (viewMonth === 1) {
      setViewYear((y) => y - 1);
      setViewMonth(12);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 12) {
      setViewYear((y) => y + 1);
      setViewMonth(1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const firstDay = new Date(viewYear, viewMonth - 1, 1);
  const lastDay = new Date(viewYear, viewMonth, 0);
  const startDow = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const calendarCells: (number | null)[] = [];
  for (let i = 0; i < startDow; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  const totalHomeGames = 81;
  const pastGames = fightersSchedule2026.filter((g) => g.date < todayStr).length;
  const specialGames = fightersSchedule2026.filter((g) => g.specialEvent).length;

  return (
    <section id="schedule" className="bg-background-100 py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="text-center">
          <span className="font-heading italic text-accent-600 text-sm tracking-[0.3em] uppercase">
            Fighters Home Games
          </span>
          <h2 className="mt-4 font-heading text-2xl md:text-4xl lg:text-5xl leading-tight text-foreground-950">
            2026 ファイターズ<br />
            <span className="italic">ホームゲーム</span>日程
          </h2>
          <p className="mt-4 text-foreground-700 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
            エスコンフィールドHOKKAIDOで開催される試合日は駐車場が大変混雑します。アネットパーキングなら
            渋滞知らずでスタジアムまで直行できます。
          </p>
        </div>

        <div className="mt-12 md:mt-16 flex flex-col lg:flex-row gap-8 md:gap-10">
          {/* Calendar Panel */}
          <div className="lg:w-[420px] shrink-0">
            <div className="bg-background-50 rounded-3xl overflow-hidden">
              {/* Month selector */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
                <button
                  onClick={goToPrevMonth}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-600 hover:text-foreground-950 cursor-pointer transition-colors"
                  aria-label="前の月"
                >
                  <i className="ri-arrow-left-s-line text-lg" />
                </button>
                <span className="font-heading text-lg text-foreground-950">
                  {viewYear}年{monthLabels[viewMonth]}
                </span>
                <button
                  onClick={goToNextMonth}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-600 hover:text-foreground-950 cursor-pointer transition-colors"
                  aria-label="次の月"
                >
                  <i className="ri-arrow-right-s-line text-lg" />
                </button>
              </div>

              {/* Calendar grid */}
              <div className="p-4">
                <div className="grid grid-cols-7 mb-2">
                  {DAYS_OF_WEEK.map((dow) => (
                    <div
                      key={dow}
                      className={`text-center text-[11px] font-medium py-1 ${
                        dow === "日"
                          ? "text-red-500"
                          : dow === "土"
                            ? "text-blue-500"
                            : "text-foreground-500"
                      }`}
                    >
                      {dow}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-y-1">
                  {calendarCells.map((day, idx) => {
                    if (day === null) {
                      return <div key={`empty-${idx}`} className="min-h-[4.25rem]" />;
                    }
                    const dateStr = formatDateStr(viewYear, viewMonth, day);
                    const game = gamesMap.get(dateStr);
                    const isToday = dateStr === todayStr;
                    const isPast = dateStr < todayStr;
                    const dowIdx = new Date(viewYear, viewMonth - 1, day).getDay();
                    const isSunday = dowIdx === 0;
                    const isSaturday = dowIdx === 6;
                    const isHoliday = game?.isHoliday;
                    const hasSpecial = game?.specialEvent;
                    const shortName = game ? (opponentShortNames[game.opponent] || game.opponent) : null;

                    return (
                      <div
                        key={dateStr}
                        onClick={game ? () => setSelectedGame(game) : undefined}
                        className={`min-h-[4.25rem] flex flex-col items-center justify-center py-1 relative ${game ? "cursor-pointer hover:bg-background-200/40 rounded-xl transition-colors" : ""}`}
                      >
                        <span
                          className={`text-sm leading-none ${
                            isToday
                              ? "font-bold text-primary-600"
                              : isHoliday || isSunday
                                ? "text-red-500"
                                : isSaturday
                                  ? "text-blue-500"
                                  : "text-foreground-800"
                          } ${isPast && !isToday ? "opacity-40" : ""}`}
                        >
                          {day}
                        </span>
                        {game && (
                          <>
                            {hasSpecial && (
                              <span
                                className={`mt-0.5 w-3.5 h-3.5 flex items-center justify-center ${isPast ? "opacity-40" : ""}`}
                                title={`${game.opponent} - ${game.specialEvent}`}
                              >
                                <i className={`${specialEventStyles[game.specialEvent!]?.icon || "ri-star-line"} text-[10px] ${specialEventStyles[game.specialEvent!]?.text || "text-accent-600"}`} />
                              </span>
                            )}
                            <span
                              className={`mt-0.5 text-[10px] leading-tight font-medium whitespace-nowrap ${
                                isPast ? "opacity-40" : "text-foreground-700"
                              }`}
                            >
                              {shortName}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-4 grid grid-cols-4 gap-3">
              <div className="bg-background-50 rounded-2xl p-3 text-center">
                <div className="text-xl font-heading text-foreground-950">
                  {totalHomeGames}
                </div>
                <div className="text-[11px] text-foreground-600 mt-0.5">
                  総試合数
                </div>
              </div>
              <div className="bg-background-50 rounded-2xl p-3 text-center">
                <div className="text-[11px] text-foreground-600 mt-0.5">
                  終了
                </div>
              </div>
              <div className="bg-background-50 rounded-2xl p-3 text-center">
                <div className="text-[11px] text-foreground-600 mt-0.5">
                  残り
                </div>
              </div>
              <div className="bg-background-50 rounded-2xl p-3 text-center">
                <div className="text-[11px] text-foreground-600 mt-0.5">
                  特別企画
                </div>
              </div>
            </div>
          </div>

          {/* Game List */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg text-foreground-950">
                {viewYear}年{viewMonth}月の開催試合
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSpecialOnly(!showSpecialOnly)}
                  className={`text-xs rounded-full px-3 py-1.5 whitespace-nowrap cursor-pointer transition-colors ${
                    showSpecialOnly
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-50 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <i className="ri-magic-line mr-1" />
                  特別企画のみ
                </button>
                <span className="text-xs text-foreground-600 bg-background-50 rounded-full px-3 py-1">
                  {displayedGames.length}試合
                </span>
              </div>
            </div>

            {displayedGames.length === 0 ? (
              <div className="bg-background-50 rounded-3xl p-10 text-center">
                <span className="w-14 h-14 flex items-center justify-center mx-auto rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-calendar-2-line text-2xl" />
                </span>
                <p className="mt-4 text-foreground-600 text-sm">
                  {showSpecialOnly ? "今月の特別企画試合はありません" : "今月のホームゲームはありません"}
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                {displayedGames.map((game) => {
                  const isPast = game.date < todayStr;
                  const isToday = game.date === todayStr;
                  const dateObj = new Date(game.date);
                  const md = `${dateObj.getMonth() + 1}/${dateObj.getDate()}`;
                  const ticketStatus = getTicketStatus(game, todayStr);
                  const eventStyle = game.specialEvent ? specialEventStyles[game.specialEvent] : null;

                  return (
                    <div
                      key={game.date}
                      onClick={() => setSelectedGame(game)}
                      className={`flex flex-col bg-background-50 rounded-2xl px-4 py-3.5 transition-colors cursor-pointer hover:bg-background-200/50 ${
                        isToday ? "ring-2 ring-primary-500" : ""
                      } ${isPast ? "opacity-50" : ""}`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Date & Time */}
                        <div className="w-14 shrink-0 text-center">
                          <div
                            className={`text-xs ${
                              game.isHoliday || game.dayOfWeek === "日"
                                ? "text-red-500"
                                : game.dayOfWeek === "土"
                                  ? "text-blue-500"
                                  : "text-foreground-500"
                            }`}
                          >
                            {md}（{game.dayOfWeek}）
                          </div>
                          <div className="text-lg font-heading text-foreground-950 mt-0.5">
                            {game.time}
                          </div>
                        </div>

                        {/* Opponent + Special Event */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span
                            className={`w-3 h-3 rounded-full shrink-0 ${opponentColors[game.opponent] || "bg-foreground-600"}`}
                          />
                          <span className="text-sm text-foreground-800 whitespace-nowrap">
                            {game.matchLabel ? game.matchLabel : `vs ${game.opponent}`}
                          </span>
                          {eventStyle && (
                            <span
                              className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${eventStyle.bg} ${eventStyle.text}`}
                            >
                              <span className="w-3 h-3 flex items-center justify-center">
                                <i className={`${eventStyle.icon} text-[11px]`} />
                              </span>
                              {eventStyle.label}
                            </span>
                          )}
                        </div>

                        {/* Ticket status */}
                        {ticketStatus && (
                          <span
                            className={`text-[11px] font-medium rounded-full px-2.5 py-0.5 whitespace-nowrap shrink-0 ${
                              ticketStatus.urgent
                                ? "bg-secondary-100 text-secondary-700"
                                : "bg-emerald-100 text-emerald-700"
                            }`}
                          >
                            <span className="w-3 h-3 inline-flex items-center justify-center mr-0.5">
                              <i className="ri-ticket-line text-[11px]" />
                            </span>
                            {ticketStatus.label}
                          </span>
                        )}

                        {isToday && (
                          <span className="text-[11px] text-primary-600 font-medium bg-primary-100 rounded-full px-2.5 py-0.5 whitespace-nowrap shrink-0">
                            本日
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Special Events Legend */}
            <div className="mt-6">
              <p className="text-xs text-foreground-500 mb-2 font-medium">特別企画アイコン</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(specialEventStyles).map(([key, style]) => (
                  <span
                    key={key}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full ${style.bg} ${style.text}`}
                  >
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className={`${style.icon} text-[11px]`} />
                    </span>
                    {style.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Opponent Legend */}
            <div className="mt-4 flex flex-wrap gap-3">
              {Object.entries(opponentShortNames).map(([full, short]) => (
                <div key={full} className="flex items-center gap-1.5 text-xs text-foreground-600">
                  <span className={`w-2.5 h-2.5 rounded-full ${opponentColors[full] || "bg-foreground-600"}`} />
                  {short}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <p className="text-xs text-foreground-500 mb-4">
            ※試合日程・特別企画・チケット販売日は予告なく変更される場合があります。最新情報はファイターズ公式サイトをご確認ください。
          </p>
          <a
            href="#reserve"
            className="inline-flex items-center gap-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full px-8 py-3.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-calendar-check-line" />
            試合日の駐車場を予約する
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50 text-primary-600">
              <i className="ri-arrow-right-line text-sm" />
            </span>
          </a>
        </div>
      </div>

      {/* Game Detail Modal */}
      {selectedGame && (
        <GameDetailModal
          game={selectedGame}
          isPast={selectedGame.date < todayStr}
          isToday={selectedGame.date === todayStr}
          onClose={() => setSelectedGame(null)}
        />
      )}
    </section>
  );
}